import React, { useState } from 'react';
import { Search, Plus, Grid, List, Filter } from 'lucide-react';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Select } from '../components/common/Select';
import { ArtistCard } from '../components/artists/ArtistCard';
import { ArtistForm } from '../components/artists/ArtistForm';
import { ArtistDetails } from '../components/artists/ArtistDetails';
import { useToast } from '../components/common/Toast';
import { useAppData } from '../context/DataContext';
import { Artist } from '../types';

export const Artists: React.FC = () => {
  const { artists, addArtist, updateArtist, deleteArtist } = useAppData();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'totalSongs' | 'totalStreams' | 'totalRevenue' | 'joinedDate'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [editingArtist, setEditingArtist] = useState<Artist | null>(null);
  const { addToast } = useToast();

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'totalSongs', label: 'Total Songs' },
    { value: 'totalStreams', label: 'Total Streams' },
    { value: 'totalRevenue', label: 'Total Revenue' },
    { value: 'joinedDate', label: 'Join Date' }
  ];

  const filteredAndSortedArtists = React.useMemo(() => {
    let filtered = artists.filter(artist =>
      artist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      artist.bio.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'totalSongs':
          aValue = a.totalSongs;
          bValue = b.totalSongs;
          break;
        case 'totalStreams':
          aValue = a.totalStreams;
          bValue = b.totalStreams;
          break;
        case 'totalRevenue':
          aValue = a.totalRevenue;
          bValue = b.totalRevenue;
          break;
        case 'joinedDate':
          aValue = new Date(a.joinedDate).getTime();
          bValue = new Date(b.joinedDate).getTime();
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [artists, searchQuery, sortBy, sortOrder]);

  const handleAddArtist = () => {
    setEditingArtist(null);
    setShowForm(true);
  };

  const handleEditArtist = (artist: Artist) => {
    setEditingArtist(artist);
    setShowForm(true);
    setShowDetails(false);
  };

  const handleDeleteArtist = (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    if (artist) {
      deleteArtist(artistId);
      addToast({
        type: 'success',
        title: 'Artist Deleted',
        message: `${artist.name} has been removed from your roster.`
      });
    }
  };

  const handleViewDetails = (artist: Artist) => {
    setSelectedArtist(artist);
    setShowDetails(true);
  };

  const handleSaveArtist = (artistData: Partial<Artist>) => {
    if (editingArtist) {
      // Update existing artist
      updateArtist(editingArtist.id, artistData);
      addToast({
        type: 'success',
        title: 'Artist Updated',
        message: `${artistData.name} has been updated successfully.`
      });
    } else {
      // Add new artist
      const newArtist: Artist = {
        id: Date.now().toString(),
        name: artistData.name || '',
        bio: artistData.bio || '',
        image: artistData.image || '',
        socialLinks: artistData.socialLinks || {
          instagram: '',
          twitter: '',
          facebook: '',
          spotify: '',
          youtube: ''
        },
        contactInfo: artistData.contactInfo || {
          email: '',
          phone: ''
        },
        totalSongs: 0,
        totalStreams: 0,
        totalRevenue: 0,
        joinedDate: new Date().toISOString()
      };
      
      addArtist(newArtist);
      addToast({
        type: 'success',
        title: 'Artist Added',
        message: `${newArtist.name} has been added to your roster.`
      });
    }
  };

  const getTotalStats = () => {
    return {
      totalArtists: artists.length,
      totalSongs: artists.reduce((sum, artist) => sum + artist.totalSongs, 0),
      totalStreams: artists.reduce((sum, artist) => sum + artist.totalStreams, 0),
      totalRevenue: artists.reduce((sum, artist) => sum + artist.totalRevenue, 0)
    };
  };

  const stats = getTotalStats();

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center p-4 bg-blue-500/10 border-blue-500/20">
          <div className="text-2xl font-bold text-blue-400 mb-1">
            {stats.totalArtists}
          </div>
          <div className="text-sm text-gray-400">Total Artists</div>
        </Card>
        <Card className="text-center p-4 bg-green-500/10 border-green-500/20">
          <div className="text-2xl font-bold text-green-400 mb-1">
            {stats.totalSongs}
          </div>
          <div className="text-sm text-gray-400">Total Songs</div>
        </Card>
        <Card className="text-center p-4 bg-purple-500/10 border-purple-500/20">
          <div className="text-2xl font-bold text-purple-400 mb-1">
            {formatNumber(stats.totalStreams)}
          </div>
          <div className="text-sm text-gray-400">Total Streams</div>
        </Card>
        <Card className="text-center p-4 bg-gold-500/10 border-gold-500/20">
          <div className="text-2xl font-bold text-gold-400 mb-1">
            ${stats.totalRevenue.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">Total Revenue</div>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex-1 min-w-64">
              <Input
                placeholder="Search artists..."
                value={searchQuery}
                onChange={setSearchQuery}
                icon={Search}
              />
            </div>
            <Select
              value={sortBy}
              onChange={(value) => setSortBy(value as any)}
              options={sortOptions}
              className="min-w-40"
            />
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
          </div>
          
          <div className="flex gap-2">
            <div className="flex bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gold-500 text-black' : 'text-gray-400 hover:text-white'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gold-500 text-black' : 'text-gray-400 hover:text-white'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <Button onClick={handleAddArtist} icon={Plus}>
              Add Artist
            </Button>
          </div>
        </div>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>
          Showing {filteredAndSortedArtists.length} of {artists.length} artists
        </span>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="text-gold-400 hover:text-gold-300"
          >
            Clear search
          </button>
        )}
      </div>

      {/* Artists Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAndSortedArtists.map((artist) => (
            <ArtistCard
              key={artist.id}
              artist={artist}
              onEdit={handleEditArtist}
              onDelete={handleDeleteArtist}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="divide-y divide-gray-700">
            {filteredAndSortedArtists.map((artist) => (
              <div key={artist.id} className="p-6 hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-white">{artist.name}</h3>
                      <p className="text-sm text-gray-400 line-clamp-1">{artist.bio}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm">
                        <span className="text-blue-400">{artist.totalSongs} songs</span>
                        <span className="text-green-400">{formatNumber(artist.totalStreams)} streams</span>
                        <span className="text-gold-400">${artist.totalRevenue.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(artist)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditArtist(artist)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {filteredAndSortedArtists.length === 0 && (
        <Card className="text-center p-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center">
            {searchQuery ? (
              <Search className="w-8 h-8 text-gray-400" />
            ) : (
              <Plus className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {searchQuery ? 'No artists found' : 'No artists yet'}
          </h3>
          <p className="text-gray-400 mb-6">
            {searchQuery 
              ? 'Try adjusting your search query to find more artists.'
              : 'Start building your roster by adding your first artist.'}
          </p>
          <Button onClick={handleAddArtist} icon={Plus}>
            Add Artist
          </Button>
        </Card>
      )}

      {/* Artist Form Modal */}
      <ArtistForm
        artist={editingArtist || undefined}
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingArtist(null);
        }}
        onSave={handleSaveArtist}
      />

      {/* Artist Details Modal */}
      <ArtistDetails
        artist={selectedArtist}
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedArtist(null);
        }}
        onEdit={handleEditArtist}
      />
    </div>
  );
};